/* Solis site-wide enhancements.
 * Loaded from layout/theme.liquid; runs on every page.
 *
 * Responsibilities
 *   1. Scroll progress indicator — a thin bar at the bottom of the viewport
 *      that fills as the visitor scrolls. Appears after 10% progress so short
 *      pages don't flash it; respects prefers-reduced-motion.
 *   2. Hash-link scroll fix for V2 Discover in-page anchors.
 *   3. Scroll-to-top button on long Discover (.dc-root) pages. A plain fixed
 *      button — NOT sticky, NOT scroll-capturing — so it cannot reproduce the
 *      iOS WebKit scroll-lock class of bug (see docs/modules/discovery.md).
 *   4. On the .com storefront only, keep editorial anchors on .com when a
 *      third-party/theme surface emits absolute .co.uk /pages/ or /blogs/ URLs.
 */

(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  function initScrollProgress() {
    // Short pages don't need a progress bar.
    if (document.documentElement.scrollHeight <= window.innerHeight * 1.5) return;

    var bar = document.createElement('div');
    bar.className = 'solis-progress';
    bar.setAttribute('aria-hidden', 'true');
    var fill = document.createElement('div');
    fill.className = 'solis-progress__fill';
    bar.appendChild(fill);
    document.body.appendChild(bar);

    var ticking = false;

    function update() {
      ticking = false;
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) { bar.classList.remove('is-visible'); return; }
      var scrolled = window.scrollY || doc.scrollTop || 0;
      var pct = Math.min(1, Math.max(0, scrolled / max));
      // Reveal only once the visitor has scrolled past ~10% — avoids a
      // flicker on pages that fit within a screen or two.
      if (pct >= 0.1) {
        bar.classList.add('is-visible');
      } else {
        bar.classList.remove('is-visible');
      }
      fill.style.width = (pct * 100).toFixed(2) + '%';
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /**
   * Fix hash-link scrolling on V2 discover pages. The Impact theme's bundled
   * click interceptor preventDefaults anchor clicks (likely for smooth-scroll
   * or instant-nav) but doesn't actually scroll for our in-page anchors,
   * leaving the quick-paths chips dead. Take over in the capture phase so
   * our handler wins before the theme's handler cancels the event.
   */
  function initHashScrollFix() {
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (!a) return;
      // Only rescue links we own. Anything else is free to use the theme's
      // native smooth-scroll if it works.
      if (!a.matches('.dc-quick-paths__chip, .dc-section__back-link, .dc-top-nav__link, .dc-movement-pill, .dc-era-nav__link, .dc-context-links__chip')) return;
      var href = a.getAttribute('href');
      if (!href || href === '#' || href.length < 2) return;
      var target = document.getElementById(href.slice(1));
      if (!target) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      // scrollIntoView with behavior:'smooth' silently no-ops in some Chrome
      // contexts (observed in the Claude MCP browser, may also affect some
      // extension profiles). 'instant' is the one value that reliably scrolls
      // across the environments we care about; scroll-margin-top on the
      // target section handles the sticky-header offset.
      target.scrollIntoView({ behavior: 'instant', block: 'start' });
      if (history.replaceState) {
        history.replaceState(null, '', a.pathname + a.search + href);
      }
    }, true);
  }

  /**
   * The same Shopify pages/articles are served on .co.uk and .com. Canonicals,
   * hreflang, and JSON-LD can legitimately mention .co.uk, but clickable
   * editorial anchors should keep a .com visitor on .com.
   */
  function initComEditorialLinkRewrite() {
    if (!window.location || !/(^|\.)solisprints\.com$/i.test(window.location.hostname)) return;

    var editorialPath = /^\/(?:pages|blogs)\//;
    var ukHost = /^(?:www\.)?solisprints\.co\.uk$/i;

    function rewriteAnchor(anchor) {
      var raw = anchor && anchor.getAttribute ? anchor.getAttribute('href') : '';
      if (!raw || raw.charAt(0) === '#') return false;

      try {
        var url = new URL(raw, window.location.href);
        if (!ukHost.test(url.hostname) || !editorialPath.test(url.pathname)) return false;
        anchor.setAttribute('href', url.pathname + url.search + url.hash);
        return true;
      } catch (_) {
        return false;
      }
    }

    function rewriteTree(root) {
      if (!root) return;
      if (root.matches && root.matches('a[href]')) rewriteAnchor(root);
      var anchors = root.querySelectorAll ? root.querySelectorAll('a[href]') : [];
      for (var i = 0; i < anchors.length; i++) rewriteAnchor(anchors[i]);
    }

    rewriteTree(document);

    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (a) rewriteAnchor(a);
    }, true);

    if (window.MutationObserver) {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var nodes = mutations[i].addedNodes || [];
          for (var j = 0; j < nodes.length; j++) {
            if (nodes[j].nodeType === 1) rewriteTree(nodes[j]);
          }
        }
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  /**
   * Scroll-to-top button for long Discover editorial pages. Fades in once the
   * visitor is a screenful or so down, returns them to the top on click.
   * Scoped to .dc-root pages and long pages only. A plain fixed button — it
   * never captures scroll, so it stays clear of the iOS scroll-lock failure mode.
   */
  function initScrollToTop() {
    // Discover editorial pages only — that's where the long pages live.
    if (!document.querySelector('.dc-root')) return;
    // Short pages don't need it.
    if (document.documentElement.scrollHeight <= window.innerHeight * 2) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dc-to-top';
    btn.setAttribute('aria-label', 'Scroll back to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V6M6 12l6-6 6 6"/></svg>';
    document.body.appendChild(btn);

    var ticking = false;
    function update() {
      ticking = false;
      var scrolled = window.scrollY || document.documentElement.scrollTop || 0;
      // Reveal after ~1.25 screens — past the hero/intro, into the long body.
      if (scrolled > window.innerHeight * 1.25) {
        btn.classList.add('is-visible');
      } else {
        btn.classList.remove('is-visible');
      }
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /**
   * Country filter chips on the WTS flat museum list. Clicking a chip filters
   * the cards to that country (and opens the View-all <details> so tail matches
   * show too); "All" restores the top-10 + re-collapses the tail.
   */
  function initWtsCountryFilter() {
    var nav = document.querySelector('.dc-wts-filter');
    if (!nav) return;
    var cards = [].slice.call(document.querySelectorAll('.dc-wts-list .dc-museum-card-v2[data-country]'));
    if (!cards.length) return;
    var details = document.querySelector('.dc-wts-list .dc-wts-more');
    nav.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-country]') : null;
      if (!btn) return;
      var country = btn.getAttribute('data-country');
      var chips = nav.querySelectorAll('[data-country]');
      for (var i = 0; i < chips.length; i++) chips[i].classList.toggle('is-active', chips[i] === btn);
      if (country === 'all') {
        cards.forEach(function (c) { c.classList.remove('is-hidden'); });
        if (details) details.open = false;
      } else {
        if (details) details.open = true;
        cards.forEach(function (c) { c.classList.toggle('is-hidden', c.getAttribute('data-country') !== country); });
      }
    });
  }

  function boot() {
    try { initScrollProgress(); } catch (_) { /* never let a UX enhancement break the page */ }
    try { initHashScrollFix(); } catch (_) { /* same */ }
    try { initComEditorialLinkRewrite(); } catch (_) { /* same */ }
    try { initScrollToTop(); } catch (_) { /* same */ }
    try { initWtsCountryFilter(); } catch (_) { /* same */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
